import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  People as PeopleIcon,
  Security as SecurityIcon
} from '@mui/icons-material';
import { useOrganization } from '../../contexts/OrganizationContext';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../contexts/PermissionsContext';
import MembersList from '../../components/permissions/MembersList';
import InvitationsPanel from '../../components/permissions/InvitationsPanel';
import RolesList from '../../components/permissions/RolesList';

const PermissionsSettings = () => {
  const { selectedOrganization } = useOrganization();
  const { authenticatedFetch, isDemoMode } = useAuth();
  const { can } = usePermissions();
  const [activeTab, setActiveTab] = useState(0);
  const [members, setMembers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [allPermissions, setAllPermissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const API_URL = process.env.REACT_APP_API_URL;

  const loadMembers = useCallback(async () => {
    if (!selectedOrganization) return;

    try {
      const response = await authenticatedFetch(
        `${API_URL}/api/organisations/${selectedOrganization.id}/members`
      );
      if (!response.ok) throw new Error('Failed to load members');
      const data = await response.json();
      setMembers(data);
    } catch (err) {
      console.error('Error loading members:', err);
      setError(err.message);
    }
  }, [selectedOrganization, authenticatedFetch, API_URL]);

  const loadRoles = useCallback(async () => {
    if (!selectedOrganization) return;

    try {
      const response = await authenticatedFetch(
        `${API_URL}/api/organisations/${selectedOrganization.id}/roles`
      );
      if (!response.ok) throw new Error('Failed to load roles');
      const data = await response.json();
      setRoles(data);
    } catch (err) {
      console.error('Error loading roles:', err);
      setError(err.message);
    }
  }, [selectedOrganization, authenticatedFetch, API_URL]);

  const loadAllPermissions = useCallback(async () => {
    try {
      const response = await authenticatedFetch(`${API_URL}/api/permissions`);
      if (!response.ok) throw new Error('Failed to load permissions');
      const data = await response.json();
      setAllPermissions(data);
    } catch (err) {
      console.error('Error loading permissions:', err);
      setError(err.message);
    }
  }, [authenticatedFetch, API_URL]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      await Promise.all([loadMembers(), loadRoles(), loadAllPermissions()]);
    } finally {
      setLoading(false);
    }
  }, [loadMembers, loadRoles, loadAllPermissions]);

  useEffect(() => {
    if (selectedOrganization) {
      loadData();
    }
  }, [selectedOrganization, loadData]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Get user permissions for a specific user
  const getUserPermissions = useCallback(async (userId) => {
    try {
      const response = await authenticatedFetch(
        `${API_URL}/api/organisations/${selectedOrganization.id}/user-permissions/${userId}`
      );
      if (!response.ok) throw new Error('Failed to load user permissions');
      return await response.json();
    } catch (err) {
      console.error('Error loading user permissions:', err);
      throw err;
    }
  }, [selectedOrganization, authenticatedFetch, API_URL]);

  // Assign role to user
  const assignRole = useCallback(async (userId, roleId) => {
    try {
      const response = await authenticatedFetch(
        `${API_URL}/api/organisations/${selectedOrganization.id}/user-permissions/roles`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userId, roleId })
        }
      );
      if (!response.ok) throw new Error('Failed to assign role');
      await loadMembers();
      return await response.json();
    } catch (err) {
      console.error('Error assigning role:', err);
      throw err;
    }
  }, [selectedOrganization, authenticatedFetch, API_URL, loadMembers]);

  // Revoke role from user
  const revokeRole = useCallback(async (userId, roleId) => {
    try {
      const response = await authenticatedFetch(
        `${API_URL}/api/organisations/${selectedOrganization.id}/user-permissions/users/${userId}/roles/${roleId}`,
        { method: 'DELETE' }
      );
      if (!response.ok) throw new Error('Failed to revoke role');
      await loadMembers();
    } catch (err) {
      console.error('Error revoking role:', err);
      throw err;
    }
  }, [selectedOrganization, authenticatedFetch, API_URL, loadMembers]);

  // Create role
  const createRole = useCallback(async (name, description, permissionCodes) => {
    try {
      const response = await authenticatedFetch(
        `${API_URL}/api/organisations/${selectedOrganization.id}/roles`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name, description, permissionCodes })
        }
      );
      if (!response.ok) throw new Error('Failed to create role');
      await loadRoles();
      return await response.json();
    } catch (err) {
      console.error('Error creating role:', err);
      throw err;
    }
  }, [selectedOrganization, authenticatedFetch, API_URL, loadRoles]);

  // Update role
  const updateRole = useCallback(async (roleId, name, description) => {
    try {
      const response = await authenticatedFetch(
        `${API_URL}/api/organisations/${selectedOrganization.id}/roles/${roleId}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name, description })
        }
      );
      if (!response.ok) throw new Error('Failed to update role');
      await loadRoles();
      return await response.json();
    } catch (err) {
      console.error('Error updating role:', err);
      throw err;
    }
  }, [selectedOrganization, authenticatedFetch, API_URL, loadRoles]);

  // Update role permissions
  const updateRolePermissions = useCallback(async (roleId, permissionCodes) => {
    try {
      const response = await authenticatedFetch(
        `${API_URL}/api/organisations/${selectedOrganization.id}/roles/${roleId}/permissions`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ permissionCodes })
        }
      );
      if (!response.ok) throw new Error('Failed to update role permissions');
      await loadRoles();
      return await response.json();
    } catch (err) {
      console.error('Error updating role permissions:', err);
      throw err;
    }
  }, [selectedOrganization, authenticatedFetch, API_URL, loadRoles]);

  // Delete role
  const deleteRole = useCallback(async (roleId) => {
    try {
      const response = await authenticatedFetch(
        `${API_URL}/api/organisations/${selectedOrganization.id}/roles/${roleId}`,
        { method: 'DELETE' }
      );
      if (!response.ok) throw new Error('Failed to delete role');
      await loadRoles();
    } catch (err) {
      console.error('Error deleting role:', err);
      throw err;
    }
  }, [selectedOrganization, authenticatedFetch, API_URL, loadRoles]);

  if (!selectedOrganization) {
    return (
      <Alert severity="warning">
        Please select an organization first
      </Alert>
    );
  }

  if (!can.readPermissions && !can.readRoles) {
    return (
      <Alert severity="info">
        You don't have permission to view permissions settings.
      </Alert>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {isDemoMode && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Create an account to invite people and choose what each of them can do.
        </Alert>
      )}

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Sub-tabs for Members and Roles */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange}>
          {can.readMembers && (
            <Tab
              icon={<PeopleIcon />}
              iconPosition="start"
              label="Members"
              sx={{ textTransform: 'none' }}
            />
          )}
          {can.readRoles && (
            <Tab
              icon={<SecurityIcon />}
              iconPosition="start"
              label="Roles"
              sx={{ textTransform: 'none' }}
            />
          )}
        </Tabs>
      </Box>

      {/* Tab Content */}
      {activeTab === 0 && can.readMembers && (
        <>
          <MembersList
            members={members}
            roles={roles}
            onAssignRole={assignRole}
            onRevokeRole={revokeRole}
            onGetUserPermissions={getUserPermissions}
            canModify={can.modifyPermissions && !isDemoMode}
          />
          {/* Getting somebody into the list in the first place. */}
          <InvitationsPanel roles={roles} canInvite={can.manageMembers && !isDemoMode} />
        </>
      )}

      {activeTab === 1 && can.readRoles && (
        <RolesList
          roles={roles}
          allPermissions={allPermissions}
          onCreateRole={createRole}
          onUpdateRole={updateRole}
          onUpdateRolePermissions={updateRolePermissions}
          onDeleteRole={deleteRole}
          canManage={can.manageRoles && !isDemoMode}
        />
      )}
    </Box>
  );
};

export default PermissionsSettings;
